import { SiteConfig } from '@global/utils/SiteConfig';
import { logError } from '@src/global/utils/log';
import { SpectrabaseSiteConfiguration } from '@global/requests/siteConfig/getSiteConfig';

export const getCaptchaSiteKey = async (): Promise<
  | { fullMatchedDomain: string; headerFooterUrl: string }
  | {
      headerUrl: string;
      footerUrl: string;
      headerFooterCSS: string;
      source: string;
    }
  | undefined
> => {
  const { config } = SiteConfig.getConfig();
  const { protocol, hostname } = window.location;
  const fullMatchedDomain = `${protocol}//${hostname}`;

  try {
    const siteData = config;
    if (siteData && 'footerUrl' in siteData && 'headerUrl' in siteData) {
      const { headerUrl, footerUrl, headerFooterCSS } =
        siteData as SpectrabaseSiteConfiguration;
      return {
        headerUrl: headerUrl || '',
        footerUrl: footerUrl || '',
        headerFooterCSS: headerFooterCSS || '',
        source: siteData.source,
      };
    }
    const headerFooterUrl = siteData?.headerUrl || '';
    return {
      fullMatchedDomain,
      headerFooterUrl,
    };
  } catch (err) {
    logError(`Failed to fetch site config for ${hostname}:`, err);
    return undefined;
  }
};

export const getLocalizedHeaderURL = (
  headerUrl: string,
  country: string,
  lang: string
) => {
  return headerUrl.replace(
    /\/[a-z]{2}\/[a-z]{2}\//,
    `/${country?.toLocaleLowerCase?.()}/${lang?.toLocaleLowerCase?.()}/`
  );
};

type CaptchaSeparateReturn = {
  source: string;
  headerUrl: string;
  footerUrl: string;
  headerFooterCSS?: string;
};

export const buildHeaderFooterUrl = async (): Promise<
  string | CaptchaSeparateReturn
> => {
  const { lang, iso } = SiteConfig.getConfig();

  const captchaConfig = await getCaptchaSiteKey();
  if (!captchaConfig) {
    throw new Error('Header Footer URL is undefined');
  }
  // If the site config already provides separate header/footer URLs, return it as-is
  if (
    captchaConfig &&
    'headerUrl' in captchaConfig &&
    'footerUrl' in captchaConfig
  ) {
    return captchaConfig;
  }
  // Legacy/combined behaviour: localize and return a single headerFooterUrl string
  if ('headerFooterUrl' in captchaConfig) {
    const headerFooterUrl = captchaConfig.headerFooterUrl;

    let baseUrl: string;

    if (
      headerFooterUrl.includes('{country}') &&
      headerFooterUrl.includes('{lang}')
    ) {
      baseUrl = headerFooterUrl
        .replace('{country}', iso)
        .replace('{lang}', lang);
    } else {
      baseUrl = getLocalizedHeaderURL(headerFooterUrl, iso, lang);
    }

    return baseUrl;
  }
  // If neither type matches, throw error
  throw new Error('Header Footer URL is undefined');
};

export const triggerAEMHeaderCartIcon = () => {
  const { siteId, locale } = SiteConfig.getConfig();

  document.dispatchEvent(
    new CustomEvent('wly-cart:updated', {
      detail: {
        locale,
        site: siteId,
      },
    })
  );
};

export const getInlineStyleContents = (documentNode: Document): string[] =>
  Array.from(documentNode.querySelectorAll('style'))
    .map((style) => style.textContent?.trim() ?? '')
    .filter(Boolean);

const INLINE_STYLE_MARKER = 'data-wly-aem-inline-style';

export const injectInlineStyles = (styleContents: string[]): void => {
  Array.from(new Set(styleContents.filter(Boolean))).forEach((css) => {
    const style = document.createElement('style');

    style.setAttribute(INLINE_STYLE_MARKER, 'true');
    style.textContent = css;

    document.head.appendChild(style);
  });
};
